import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Stack,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Badge,
  Button
} from '@mui/material';
import {
  Message as MessageIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import DoneIcon from '@mui/icons-material/Done';        // single tick
import DoneAllIcon from '@mui/icons-material/DoneAll';  // double tick
import { blue } from '@mui/material/colors';
import moment from 'moment';

// Enhanced Loading Spinner with MUI
const LoadingSpinner = () => (
  <Box display="flex" alignItems="center" justifyContent="center" p={5}>
    <CircularProgress size={40} />
  </Box>
);

// Enhanced Notification Medium Report Component
const NotificationMediumReport = ({
  medium,
  mediumName,
  data = [],
  loading = false,
  fields = [],
  pagination = { currentPage: 1, totalPages: 1, totalRows: 0, rowsPerPage: 15 },
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
  onExport,
  mediumColor = '#1976d2',
  icon: IconComponent = MessageIcon
}) => {
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusChip = (status) => {
    if (status === 1 || status === true) {
      return (
        <Chip
          label="Success"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    } else if (status === 0 || status === false) {
      return (
        <Chip
          label="Failed"
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        label="Unknown"
        color="default"
        size="small"
        variant="outlined"
      />
    );
  };



// const renderWhatsappTick = (value, fieldKey) => {
//   if (value === true) {
//     if (fieldKey === 'read') {
//       return <DoneAllIcon sx={{ fontSize: 18, color: blue[500] }} />;
//     }
//     if (fieldKey === 'delivered') {
//       return <DoneAllIcon sx={{ fontSize: 18, color: 'grey' }} />;
//     }
//     if (fieldKey === 'sent') {
//       return <DoneIcon sx={{ fontSize: 18, color: 'grey' }} />;
//     }
//   }
//   return '-';
// };

const formatDateWhatsappRead = (dateStr) => {
  return moment(dateStr, "YYYY-MM-DD HH:mm:ss").format("MMMM Do YYYY, h:mm A");
};

const renderWhatsappTick = (value, fieldKey, item) => {
  if (value === true) {
    const tickIcon = (() => { 
      if (fieldKey === 'read') {
        return <DoneAllIcon sx={{ fontSize: 18, color: blue[500] }} />;
      }
      if (fieldKey === 'delivered') {
        return <DoneAllIcon sx={{ fontSize: 18, color: 'grey' }} />;
      }
      if (fieldKey === 'sent') {
        return <DoneIcon sx={{ fontSize: 18, color: 'grey' }} />;
      }
      return '-';
    })();

    if (medium === 5 && fieldKey === 'read' && item.updated_at) {
      return (
        <Tooltip
          title={
              <Box
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  lineHeight: 1.4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <Box>Last Updated</Box>
                <Box sx={{ fontWeight: 400 }}>
                  {formatDateWhatsappRead(item.updated_at)}
                </Box>
              </Box>
            }
          placement="top"
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: { offset: [0, 8] }, 
                },
              ],
            },
            tooltip: {
              sx: {
                fontSize: '14px',         
                // bgcolor: '#25D366',  
                bgcolor: 'black',         
                color: '#ffffff',             
                borderRadius: '6px',       
                padding: '8px 12px',       
              },
            },
            arrow: {
              sx: {
                color: '#222',            
              },
            },
          }}
        >
      <Box sx={{ display: 'inline-flex' }}>
        {tickIcon}
      </Box>
    </Tooltip>

      );
    }

    return tickIcon;
  }
  return '-';
};



  const renderCellContent = (item, field) => {
  const value = item[field.key];

  if ((medium === 3 || medium === 5) && ['sent', 'delivered', 'read'].includes(field.key)) {
    return renderWhatsappTick(value, field.key, item);
  }

  return value === null || value === undefined ? '-' : String(value);
};


  const handlePageClick = (event, page) => {
    if (onPageChange) {
      onPageChange(medium, page);
    }
  };

  const handleRowsChange = (event) => {
    if (onRowsPerPageChange) {
      onRowsPerPageChange(medium, event.target.value);
    }
  };

  const handleDownloadClick = () => {
    if (onExport) {
      onExport(medium);
    }
  };

  return (
    <Card 
      sx={{ 
        mb: 3, 
        boxShadow: 3,
        border: `2px solid ${mediumColor}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Enhanced Header */}
      <Box 
        sx={{ 
          p: 2,
          backgroundColor: `${mediumColor}15`,
          borderBottom: `1px solid ${mediumColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: mediumColor, width: 40, height: 40 }}>
            <IconComponent />
          </Avatar>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: mediumColor,
                // fontWeight: 600,
                fontSize: '1.1rem'
              }}
            >
              {mediumName} Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {/* Total records: {pagination.totalRows || data.length} */}
              Total records:
              <Badge 
                badgeContent={pagination.totalRows || data.length} 
                // color= 'primary'
                sx={{ 
                  ml: 2,
                  "& .MuiBadge-badge": {
                    backgroundColor: mediumColor,  // your custom color
                    color: "#fff", // text color
                  }
                }}
              />
              
            </Typography>
          </Box>
          {/* <Badge 
            badgeContent={pagination.totalRows || data.length} 
            color="primary"
            sx={{ ml: 1 }}
          /> */}
        </Box>

        <Box display="flex" gap={1}>
          {/* download excel begin */}
<Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadClick}
            disabled={loading}
            size="small"
            sx={{
              borderColor: mediumColor,
              color: mediumColor,
              '&:hover': {
                borderColor: mediumColor,
                backgroundColor: `${mediumColor}10`
              },
              minWidth: 120
            }}
          >
            {/* Download
             */}
             Export
          </Button>
          {/* end */}
          {onRefresh && (
            <Tooltip title="Refresh">
              <IconButton onClick={() => onRefresh(medium)} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {/* {onExport && (
            <Tooltip title="Export">
              <IconButton onClick={() => onExport(medium)}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )} */}
          {loading && <CircularProgress size={24} />}
        </Box>
      </Box>
      
      {/* Content */}
      <CardContent sx={{ p: 0 }}>
        {loading && data.length === 0 ? (
          <LoadingSpinner />
        ) : data.length === 0 ? (
          <Box p={5} textAlign="center">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {mediumName.toLowerCase()} notifications found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria or date range
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    {fields.map((field) => (
                      <TableCell 
                        key={field.key}
                        sx={{ 
                          fontWeight: 600,
                          color: '#4a4a4a',
                          fontSize: '0.875rem'
                        }}
                      >
                        {field.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((item, index) => (
                    <TableRow 
                      key={item.id || index}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      {fields.map((field) => (
                        <TableCell 
                          key={field.key}
                          sx={{ 
                            fontSize: '0.8rem',
                            color: '#667085',
                            py: 1.5
                          }}
                        >
                          {renderCellContent(item, field)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Enhanced Pagination */}
            {pagination.totalRows > 5 && (
              <>
                <Divider />
                <Box 
                  sx={{ 
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {pagination.currentPage * pagination.rowsPerPage - pagination.rowsPerPage + 1}-
                    {Math.min(pagination.currentPage * pagination.rowsPerPage, pagination.totalRows)} of {pagination.totalRows} records
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel>Rows</InputLabel>
                      <Select
                        value={pagination.rowsPerPage}
                        label="Rows"
                        onChange={handleRowsChange}
                      >
                        {[5, 10, 15, 25, 50, 100].map(size => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {pagination.totalPages > 1 && (
                      <Pagination
                        count={pagination.totalPages}
                        page={pagination.currentPage}
                        onChange={handlePageClick}
                        // color="primary"
                         sx={{
                            "& .MuiPaginationItem-root": {
                              color: mediumColor, 
                            },
                            "& .Mui-selected": {
                              backgroundColor: mediumColor, 
                              color: "#fff",                 
                            },
                          }}
                        showFirstButton
                        showLastButton
                        size="small"
                      />
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};


export default NotificationMediumReport;